import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import Menu from '../menu';
import {
    FiCheckCircle, FiXCircle, FiClock,
    FiLayers, FiSearch, FiShield,
} from 'react-icons/fi';
import '../../styles.css';

function parseDate(dateStr) {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return null;
    return new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
}

function getLicStatus(dataexpira, expira) {
    if (expira === 'N') return 'never';
    const date = parseDate(dataexpira);
    if (!date) return 'unknown';
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const in30 = new Date(now);
    in30.setDate(in30.getDate() + 30);
    if (date < now) return 'expired';
    if (date <= in30) return 'expiring';
    return 'valid';
}

const STATUS_CFG = {
    never:    { label: 'Nunca Expira',   color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
    valid:    { label: 'Válida',          color: '#10b981', bg: '#f0fdf4', border: '#a7f3d0' },
    expiring: { label: 'Vence em Breve', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
    expired:  { label: 'Expirada',       color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
    unknown:  { label: 'Indefinido',     color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' },
};

const FILTERS = [
    { key: 'all',      label: 'Todos' },
    { key: 'valid',    label: 'Válidas' },
    { key: 'expiring', label: 'Vencendo' },
    { key: 'expired',  label: 'Expiradas' },
    { key: 'never',    label: 'Nunca Expiram' },
];

export default function Listlic() {
    const [licData,      setLicData]      = useState({});
    const [searchValue,  setSearchValue]  = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [loading,      setLoading]      = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('/lic', { auth: { username: 'Portal', password: 'Portal1163' } });
                const grouped = res.data.reduce((acc, lic) => {
                    if (!acc[lic.nome]) acc[lic.nome] = { cnpj: lic.cnpj, licenses: [] };
                    acc[lic.nome].licenses.push(lic);
                    return acc;
                }, {});
                const sorted = {};
                Object.keys(grouped).sort((a, b) => a.localeCompare(b)).forEach(k => { sorted[k] = grouped[k]; });
                setLicData(sorted);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const kpis = useMemo(() => {
        let total = 0, valid = 0, expired = 0, expiring = 0, never = 0;
        Object.values(licData).forEach(({ licenses }) => {
            licenses.forEach(lic => {
                total++;
                const s = getLicStatus(lic.dataexpira, lic.expira);
                if (s === 'valid')    valid++;
                if (s === 'expired')  expired++;
                if (s === 'expiring') expiring++;
                if (s === 'never')    never++;
            });
        });
        return { total, valid, expired, expiring, never };
    }, [licData]);

    const filteredEntries = useMemo(() => {
        return Object.entries(licData).filter(([nome, { licenses }]) => {
            if (!nome.toLowerCase().includes(searchValue.toLowerCase())) return false;
            if (statusFilter === 'all') return true;
            return licenses.some(l => getLicStatus(l.dataexpira, l.expira) === statusFilter);
        });
    }, [licData, searchValue, statusFilter]);

    return (
        <>
            <Menu />
            <div id="cs-layout">
              <main id="cs-main">
                <div style={{ maxWidth: 960, margin: '0 auto' }}>

                    {/* Page header */}
                    <div className="cs-page-header">
                        <div>
                            <span className="cs-page-title">Licenças</span>
                            <span className="cs-page-subtitle">
                                {loading ? 'Carregando...' : `${kpis.total} licenças · ${Object.keys(licData).length} empresas`}
                            </span>
                        </div>
                    </div>

                    {/* KPI Cards */}
                    <div className="cs-kpi-row">
                        <div className="cs-kpi-card cs-kpi-neutral">
                            <div className="cs-kpi-icon"><FiLayers size={20} /></div>
                            <div className="cs-kpi-body">
                                <span className="cs-kpi-value">{loading ? '—' : kpis.total}</span>
                                <span className="cs-kpi-label">Total</span>
                            </div>
                        </div>
                        <div className="cs-kpi-card cs-kpi-success">
                            <div className="cs-kpi-icon"><FiCheckCircle size={20} /></div>
                            <div className="cs-kpi-body">
                                <span className="cs-kpi-value">{loading ? '—' : kpis.valid}</span>
                                <span className="cs-kpi-label">Válidas</span>
                            </div>
                        </div>
                        <div className="cs-kpi-card" style={{ borderLeftColor: '#f59e0b' }}>
                            <div className="cs-kpi-icon" style={{ color: '#f59e0b' }}><FiClock size={20} /></div>
                            <div className="cs-kpi-body">
                                <span className="cs-kpi-value" style={{ color: '#f59e0b' }}>{loading ? '—' : kpis.expiring}</span>
                                <span className="cs-kpi-label">Vencendo em 30d</span>
                            </div>
                        </div>
                        <div className="cs-kpi-card cs-kpi-danger">
                            <div className="cs-kpi-icon"><FiXCircle size={20} /></div>
                            <div className="cs-kpi-body">
                                <span className="cs-kpi-value">{loading ? '—' : kpis.expired}</span>
                                <span className="cs-kpi-label">Expiradas</span>
                            </div>
                        </div>
                        <div className="cs-kpi-card" style={{ borderLeftColor: '#3b82f6' }}>
                            <div className="cs-kpi-icon" style={{ color: '#3b82f6' }}><FiShield size={20} /></div>
                            <div className="cs-kpi-body">
                                <span className="cs-kpi-value" style={{ color: '#3b82f6' }}>{loading ? '—' : kpis.never}</span>
                                <span className="cs-kpi-label">Nunca Expiram</span>
                            </div>
                        </div>
                    </div>

                    {/* Filters bar */}
                    <div className="cs-main-card" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 16px', marginBottom: 12 }}>
                        <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
                            <FiSearch
                                size={14}
                                style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--cs-text-muted)', pointerEvents: 'none' }}
                            />
                            <input
                                type="text"
                                placeholder="Buscar por empresa..."
                                value={searchValue}
                                onChange={e => setSearchValue(e.target.value)}
                                style={{
                                    width: '100%', paddingLeft: 32, paddingRight: 10, height: 34,
                                    border: '1px solid var(--cs-border)', borderRadius: 'var(--cs-border-radius-sm)',
                                    fontSize: 13, background: '#fff', outline: 'none', color: 'var(--cs-text)',
                                }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {FILTERS.map(f => {
                                const active = statusFilter === f.key;
                                return (
                                    <button
                                        key={f.key}
                                        onClick={() => setStatusFilter(f.key)}
                                        style={{
                                            padding: '4px 12px', fontSize: 12, fontWeight: 500,
                                            border: '1px solid', borderRadius: 20, cursor: 'pointer', transition: 'all .15s',
                                            background: active ? 'var(--cs-blue)' : '#fff',
                                            borderColor: active ? 'var(--cs-blue)' : 'var(--cs-border)',
                                            color: active ? '#fff' : 'var(--cs-text-muted)',
                                        }}
                                    >
                                        {f.label}
                                        {f.key !== 'all' && !loading && (
                                            <span style={{
                                                marginLeft: 5, fontSize: 10, fontWeight: 700,
                                                background: active ? 'rgba(255,255,255,.25)' : 'var(--cs-blue-pale)',
                                                color: active ? '#fff' : 'var(--cs-blue)',
                                                borderRadius: 10, padding: '1px 5px',
                                            }}>
                                                {kpis[f.key] ?? 0}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Content */}
                    {loading ? (
                        <div className="cs-main-card">
                            <div className="cs-srv-skeleton">
                                {[1, 2, 3].map(i => <div key={i} className="cs-skeleton-row" />)}
                            </div>
                        </div>
                    ) : filteredEntries.length === 0 ? (
                        <div className="cs-main-card cs-srv-empty" style={{ padding: 32 }}>
                            Nenhuma licença encontrada.
                        </div>
                    ) : (
                        filteredEntries.map(([nome, { cnpj, licenses }]) => {
                            const visibleLics = statusFilter === 'all'
                                ? licenses
                                : licenses.filter(l => getLicStatus(l.dataexpira, l.expira) === statusFilter);

                            if (visibleLics.length === 0) return null;

                            const hasExpired  = licenses.some(l => getLicStatus(l.dataexpira, l.expira) === 'expired');
                            const hasExpiring = licenses.some(l => getLicStatus(l.dataexpira, l.expira) === 'expiring');
                            const accentColor = hasExpired ? '#ef4444' : hasExpiring ? '#f59e0b' : '#10b981';

                            return (
                                <div key={nome} className="cs-main-card" style={{ marginBottom: 10 }}>
                                    {/* Company header */}
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: 10,
                                        marginBottom: 10, paddingBottom: 10,
                                        borderBottom: '1px solid var(--cs-border)',
                                    }}>
                                        <div style={{
                                            width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                                            background: `${accentColor}1a`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 15, fontWeight: 700, color: accentColor,
                                        }}>
                                            {nome.charAt(0).toUpperCase()}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--cs-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {nome}
                                            </div>
                                            <div style={{ fontSize: 11, color: 'var(--cs-text-muted)', marginTop: 1 }}>
                                                CNPJ: {cnpj} &middot; {licenses.length} licença{licenses.length !== 1 ? 's' : ''}
                                            </div>
                                        </div>
                                        {hasExpired && (
                                            <span style={{ fontSize: 10, fontWeight: 600, color: '#ef4444', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 20, padding: '2px 8px', flexShrink: 0 }}>
                                                Com expiradas
                                            </span>
                                        )}
                                        {!hasExpired && hasExpiring && (
                                            <span style={{ fontSize: 10, fontWeight: 600, color: '#f59e0b', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 20, padding: '2px 8px', flexShrink: 0 }}>
                                                Vencimento próximo
                                            </span>
                                        )}
                                    </div>

                                    {/* Licenses table */}
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>Sistema</th>
                                                <th style={thStyle}>Vencimento</th>
                                                <th style={thStyle}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleLics.map((lic, i) => {
                                                const status = getLicStatus(lic.dataexpira, lic.expira);
                                                const cfg    = STATUS_CFG[status] || STATUS_CFG.unknown;
                                                return (
                                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}
                                                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                    >
                                                        <td style={tdStyle}>{lic.sistema}</td>
                                                        <td style={{ ...tdStyle, color: 'var(--cs-text-muted)' }}>
                                                            {lic.expira === 'N' ? '—' : lic.dataexpira}
                                                        </td>
                                                        <td style={tdStyle}>
                                                            <span style={{
                                                                display: 'inline-block', padding: '2px 9px', borderRadius: 20,
                                                                fontSize: 11, fontWeight: 600,
                                                                color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
                                                            }}>
                                                                {cfg.label}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })
                    )}
                </div>
              </main>
            </div>
        </>
    );
}

const thStyle = {
    textAlign: 'left', padding: '4px 8px',
    fontWeight: 600, color: 'var(--cs-text-muted)',
    fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em',
    borderBottom: '1px solid var(--cs-border)',
};

const tdStyle = {
    padding: '6px 8px', color: 'var(--cs-text)',
};
